import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { 
  Settings, Save, Shield, Clock, DollarSign, Mail, Database,
  RefreshCw, CheckCircle, AlertTriangle, XCircle,
  Zap, Bell, Lock, Server, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';

const SystemSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    // General
    trial_duration_days: 3,
    starter_record_limit: 20,
    growth_record_limit: 100,
    referral_reward_days: 14,
    
    // Pricing
    starter_monthly_price: 299,
    growth_monthly_price: 799,
    pro_monthly_price: 9.90,
    
    // Feature Flags
    features: {
      referral_program: true,
      email_signature: true,
      peer_reviews: true,
      record_requests: true,
      team_comparison: true,
      pipeline: true
    },
    
    // Email Digest
    employee_digest_time: '08:00',
    employer_digest_day: 'monday',
    employer_digest_time: '09:00'
  });
  
  const [blockedDomains, setBlockedDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [integrations, setIntegrations] = useState({
    stripe: { status: 'connected', lastSync: new Date().toISOString() },
    resend: { status: 'connected', lastSync: new Date().toISOString() },
    mongodb: { status: 'connected', lastSync: new Date().toISOString() }
  });

  useEffect(() => {
    fetchSettings();
    fetchBlockedDomains();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('vett_token')}` }
      });
      const data = await res.json();
      if (data && Object.keys(data).length > 0) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedDomains = async () => {
    // In production, this would come from the backend
    setBlockedDomains([
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
      'gmx.net', 'web.de', 'googlemail.com', 'live.com', 'msn.com',
      'aol.com', 'protonmail.com', 'mail.com', 'zoho.com'
    ]);
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('vett_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = () => {
    if (newDomain && !blockedDomains.includes(newDomain.toLowerCase())) {
      setBlockedDomains([...blockedDomains, newDomain.toLowerCase()]);
      setNewDomain('');
      toast.success('Domain added to blocked list');
    }
  };

  const handleRemoveDomain = (domain) => {
    setBlockedDomains(blockedDomains.filter(d => d !== domain));
    toast.success('Domain removed from blocked list');
  };

  const handleToggleFeature = (feature) => {
    setSettings(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const saveButton = (
    <Button
      onClick={handleSaveSettings}
      disabled={saving}
      className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
    >
      {saving ? (
        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Save className="w-4 h-4 mr-2" />
      )}
      Save Changes
    </Button>
  );

  if (loading) {
    return (
      <AdminLayout title="System Settings" breadcrumbs={[{ label: 'Settings' }]}>
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-[#1B5AFF] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="System Settings" breadcrumbs={[{ label: 'Settings' }]} actions={saveButton}>
      <div data-testid="system-settings">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#FFFFFF] border border-[#E8EDF5] mb-6">
            <TabsTrigger value="general" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              General
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Feature Flags
            </TabsTrigger>
            <TabsTrigger value="blocked" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Blocked Domains
            </TabsTrigger>
            <TabsTrigger value="digest" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Email Digest
            </TabsTrigger>
            <TabsTrigger value="integrations" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Integrations
            </TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h3 className="text-[#0A0F1E] font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Trial & Plan Limits
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-[#5B6472] text-sm mb-2 block">Trial Duration (Days)</label>
                  <Input
                    type="number"
                    value={settings.trial_duration_days}
                    onChange={(e) => setSettings({ ...settings, trial_duration_days: parseInt(e.target.value) })}
                    className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                    min={1}
                    max={30}
                  />
                  <p className="text-[#5B6472] text-xs mt-1">Default trial period for new employers</p>
                </div>
                
                <div>
                  <label className="text-[#5B6472] text-sm mb-2 block">Referral Reward (Days)</label>
                  <Input
                    type="number"
                    value={settings.referral_reward_days}
                    onChange={(e) => setSettings({ ...settings, referral_reward_days: parseInt(e.target.value) })}
                    className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                    min={1}
                    max={90}
                  />
                  <p className="text-[#5B6472] text-xs mt-1">Days added for successful referrals</p>
                </div>
                
                <div>
                  <label className="text-[#5B6472] text-sm mb-2 block">Starter Plan Record Limit</label>
                  <Input
                    type="number"
                    value={settings.starter_record_limit}
                    onChange={(e) => setSettings({ ...settings, starter_record_limit: parseInt(e.target.value) })}
                    className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                    min={1}
                    max={100}
                  />
                </div>
                
                <div>
                  <label className="text-[#5B6472] text-sm mb-2 block">Growth Plan Record Limit</label>
                  <Input
                    type="number"
                    value={settings.growth_record_limit}
                    onChange={(e) => setSettings({ ...settings, growth_record_limit: parseInt(e.target.value) })}
                    className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                    min={1}
                    max={500}
                  />
                </div>
              </div>
            </Card>

            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h3 className="text-[#0A0F1E] font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                Pricing Configuration
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="text-[#5B6472] text-sm mb-2 block">Starter Price (€/month)</label>
                  <Input
                    type="number"
                    value={settings.starter_monthly_price}
                    onChange={(e) => setSettings({ ...settings, starter_monthly_price: parseInt(e.target.value) })}
                    className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                  />
                </div>
                
                <div>
                  <label className="text-[#5B6472] text-sm mb-2 block">Growth Price (€/month)</label>
                  <Input
                    type="number"
                    value={settings.growth_monthly_price}
                    onChange={(e) => setSettings({ ...settings, growth_monthly_price: parseInt(e.target.value) })}
                    className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                  />
                </div>
                
                <div>
                  <label className="text-[#5B6472] text-sm mb-2 block">Pro Employee Price (€/month)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={settings.pro_monthly_price}
                    onChange={(e) => setSettings({ ...settings, pro_monthly_price: parseFloat(e.target.value) })}
                    className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                  />
                </div>
              </div>
              <p className="text-amber-400 text-sm mt-4 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Changes to pricing will only affect new subscriptions. Existing subscriptions keep their current price.
              </p>
            </Card>
          </TabsContent>

          {/* Feature Flags */}
          <TabsContent value="features" className="space-y-6">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h3 className="text-[#0A0F1E] font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Feature Toggles
              </h3>
              <p className="text-[#5B6472] text-sm mb-6">Enable or disable platform features globally.</p>
              
              <div className="space-y-4">
                {[
                  { key: 'referral_program', name: 'Referral Program', description: 'Allow users to refer others and earn rewards' },
                  { key: 'email_signature', name: 'Email Signature Generator', description: 'Allow employees to generate branded email signatures' },
                  { key: 'peer_reviews', name: 'Peer Reviews', description: 'Allow employees to request reviews from peers' },
                  { key: 'record_requests', name: 'Record Requests', description: 'Allow employees to request records from past employers' },
                  { key: 'team_comparison', name: 'Team Comparison', description: 'Allow employers to compare team members' },
                  { key: 'pipeline', name: 'Candidate Pipeline', description: 'Allow employers to manage candidate pipelines' }
                ].map((feature) => (
                  <div key={feature.key} className="flex items-center justify-between p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <div>
                      <p className="text-[#0A0F1E] font-medium">{feature.name}</p>
                      <p className="text-[#5B6472] text-sm">{feature.description}</p>
                    </div>
                    <Switch
                      checked={settings.features?.[feature.key] ?? true}
                      onCheckedChange={() => handleToggleFeature(feature.key)}
                    />
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Blocked Domains */}
          <TabsContent value="blocked" className="space-y-6">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h3 className="text-[#0A0F1E] font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                Blocked Email Domains
              </h3>
              <p className="text-[#5B6472] text-sm mb-6">
                These domains are blocked from employer registration. Personal email addresses will be rejected.
              </p>
              
              {/* Add Domain */}
              <div className="flex gap-3 mb-6">
                <Input
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="Enter domain to block (e.g., example.com)"
                  className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] flex-1"
                />
                <Button
                  onClick={handleAddDomain}
                  className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
              
              {/* Domain List */}
              <div className="flex flex-wrap gap-2">
                {blockedDomains.map((domain) => (
                  <Badge 
                    key={domain}
                    className="bg-red-500/20 text-red-400 border-0 flex items-center gap-1 px-3 py-1"
                  >
                    {domain}
                    <button
                      onClick={() => handleRemoveDomain(domain)}
                      className="ml-1 hover:text-[#0A0F1E]"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Email Digest */}
          <TabsContent value="digest" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <h3 className="text-[#0A0F1E] font-semibold mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-purple-400" />
                  Employee Daily Digest
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-[#5B6472] text-sm mb-2 block">Send Time (UTC)</label>
                    <Input
                      type="time"
                      value={settings.employee_digest_time}
                      onChange={(e) => setSettings({ ...settings, employee_digest_time: e.target.value })}
                      className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                    />
                  </div>
                  <p className="text-[#5B6472] text-sm">
                    Daily digest includes: Unread messages, pending records, upcoming reviews, VETT Score updates.
                  </p>
                </div>
              </Card>

              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <h3 className="text-[#0A0F1E] font-semibold mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-400" />
                  Employer Weekly Digest
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-[#5B6472] text-sm mb-2 block">Send Day</label>
                    <select
                      value={settings.employer_digest_day}
                      onChange={(e) => setSettings({ ...settings, employer_digest_day: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5] text-[#0A0F1E] focus:border-[#1B5AFF] outline-none"
                    >
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => (
                        <option key={day} value={day}>{day.charAt(0).toUpperCase() + day.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#5B6472] text-sm mb-2 block">Send Time (UTC)</label>
                    <Input
                      type="time"
                      value={settings.employer_digest_time}
                      onChange={(e) => setSettings({ ...settings, employer_digest_time: e.target.value })}
                      className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                    />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Integrations */}
          <TabsContent value="integrations" className="space-y-6">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h3 className="text-[#0A0F1E] font-semibold mb-4 flex items-center gap-2">
                <Server className="w-5 h-5 text-cyan-400" />
                Integration Health
              </h3>
              <div className="space-y-4">
                {Object.entries(integrations).map(([name, info]) => (
                  <div key={name} className="flex items-center justify-between p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        info.status === 'connected' ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {name === 'stripe' && <DollarSign className="w-5 h-5 text-green-400" />}
                        {name === 'resend' && <Mail className="w-5 h-5 text-purple-400" />}
                        {name === 'mongodb' && <Database className="w-5 h-5 text-green-400" />}
                      </div>
                      <div>
                        <p className="text-[#0A0F1E] font-medium capitalize">{name}</p>
                        <p className="text-[#5B6472] text-xs">
                          Last sync: {new Date(info.lastSync).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Badge className={
                      info.status === 'connected' ? 'bg-green-500/20 text-green-400 border-0' :
                      info.status === 'error' ? 'bg-red-500/20 text-red-400 border-0' :
                      'bg-amber-500/20 text-amber-400 border-0'
                    }>
                      {info.status === 'connected' && <CheckCircle className="w-3 h-3 mr-1" />}
                      {info.status === 'error' && <XCircle className="w-3 h-3 mr-1" />}
                      {info.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>

            {/* API Keys Status */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <h3 className="text-[#0A0F1E] font-semibold mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-400" />
                API Keys Status
              </h3>
              <p className="text-[#5B6472] text-sm mb-4">
                API keys are stored securely and cannot be viewed here. Contact your system administrator to update keys.
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#FFFFFF]">
                  <span className="text-[#0A0F1E]">STRIPE_SECRET_KEY</span>
                  <Badge className="bg-green-500/20 text-green-400 border-0">Configured</Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#FFFFFF]">
                  <span className="text-[#0A0F1E]">RESEND_API_KEY</span>
                  <Badge className="bg-green-500/20 text-green-400 border-0">Configured</Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#FFFFFF]">
                  <span className="text-[#0A0F1E]">MONGO_URL</span>
                  <Badge className="bg-green-500/20 text-green-400 border-0">Configured</Badge>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default SystemSettings;
